import React, { useState } from 'react';
import { authClient } from '../lib/auth';
import { buildSocialCallbackUrls, AuthMode } from '../utils/authCallbackUrls';
import './GoogleAuthButton.css';

/** Official Google "G" mark. Inlined so there is no external asset request. */
const GoogleIcon: React.FC = () => (
  <svg className="auth-google-icon" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
    <path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
  </svg>
);

interface GoogleAuthButtonProps {
  mode: AuthMode;
  /**
   * Where the rider should land after the OAuth round trip. Defaults to the
   * saved `bikendure_return_url`, which is what the standalone auth pages want.
   * Callers embedded mid-funnel pass their own destination instead.
   */
  returnUrl?: string;
  /**
   * Run — and awaited — before the browser leaves for Google. OAuth is a full
   * page load, so anything held only in component state must be persisted here
   * or it is gone on return. A rejection aborts the redirect and is surfaced.
   */
  onBeforeRedirect?: () => Promise<void>;
}

const GoogleAuthButton: React.FC<GoogleAuthButtonProps> = ({ mode, returnUrl, onBeforeRedirect }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleClick() {
    setError('');
    setLoading(true);

    if (onBeforeRedirect) {
      try {
        await onBeforeRedirect();
      } catch (err) {
        console.error('Failed to save state before Google redirect:', err);
        setError('Your answers could not be saved, so we stopped before sending you to Google. Please try again.');
        setLoading(false);
        return;
      }
    }

    const destination = returnUrl ?? sessionStorage.getItem('bikendure_return_url') ?? '/';
    const urls = buildSocialCallbackUrls(window.location.origin, destination, mode);

    try {
      const result = await authClient.signIn.social({ provider: 'google', ...urls });
      // On success the browser navigates away to Google, so `loading` is left
      // true on purpose — the button must not flicker back to its idle state.
      if (result?.error) {
        setError(result.error.message ?? 'Could not start Google sign-in. Please try again.');
        setLoading(false);
      }
    } catch {
      setError('Could not reach Google. Please try again.');
      setLoading(false);
    }
  }

  return (
    <>
      <button type="button" className="auth-google" onClick={handleClick} disabled={loading}>
        <GoogleIcon />
        <span>Continue with Google</span>
      </button>
      {error && <p className="auth-error auth-google-error">{error}</p>}
    </>
  );
};

export default GoogleAuthButton;
